import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, LoginCredentials, LoginResponse, RegisterData, User } from '../../types/auth.types';

export const AUTH_STORAGE_KEYS = {
  accessToken: 'access_token',
  refreshToken: 'refresh_token',
  user: 'user',
};

const LEGACY_AUTH_STORAGE_KEYS = ['authToken', 'refreshToken'];

const readStoredUser = (): User | null => {
  const storedUser = localStorage.getItem(AUTH_STORAGE_KEYS.user);
  if (!storedUser) return null;

  try {
    return JSON.parse(storedUser) as User;
  } catch {
    localStorage.removeItem(AUTH_STORAGE_KEYS.user);
    return null;
  }
};

export const getStoredAccessToken = () => localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
export const getStoredRefreshToken = () => localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);

const getAccessTokenFromResponse = (data: LoginResponse) => (
  data.access || data.access_token || data.token || null
);

const getRefreshTokenFromResponse = (data: LoginResponse) => (
  data.refresh || data.refresh_token || data.refreshToken || null
);

const persistAuth = (data: LoginResponse) => {
  const accessToken = getAccessTokenFromResponse(data);
  const refreshToken = getRefreshTokenFromResponse(data);

  if (accessToken) localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, accessToken);
  if (refreshToken) localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, refreshToken);
  if (data.user) localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(data.user));

  LEGACY_AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

const clearStoredAuth = () => {
  localStorage.removeItem(AUTH_STORAGE_KEYS.accessToken);
  localStorage.removeItem(AUTH_STORAGE_KEYS.refreshToken);
  localStorage.removeItem(AUTH_STORAGE_KEYS.user);
  LEGACY_AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

const applyAuthPayload = (state: AuthState, payload: LoginResponse) => {
  const accessToken = getAccessTokenFromResponse(payload);
  const refreshToken = getRefreshTokenFromResponse(payload);

  state.user = payload.user || state.user || readStoredUser();
  state.token = accessToken;
  state.refreshToken = refreshToken;
  state.isAuthenticated = Boolean(accessToken);
  state.error = null;
};

export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/token/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message || error.detail || 'Erreur de connexion');
      }

      const data: LoginResponse = await response.json();
      persistAuth(data);

      return data;
    } catch (error) {
      return rejectWithValue('Erreur réseau');
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData: RegisterData, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/auth/registration/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message || error.detail || 'Erreur d\'inscription');
      }

      const data: LoginResponse = await response.json();
      persistAuth(data);

      return data;
    } catch (error) {
      return rejectWithValue('Erreur réseau');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async () => {
    try {
      const refresh = getStoredRefreshToken();
      await fetch('/api/auth/logout/', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getStoredAccessToken() || ''}`,
          'Content-Type': 'application/json',
        },
        body: refresh ? JSON.stringify({ refresh }) : undefined,
      });
    } finally {
      clearStoredAuth();
    }

    return null;
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refresh',
  async (_, { rejectWithValue }) => {
    try {
      const storedRefreshToken = getStoredRefreshToken();
      if (!storedRefreshToken) {
        return rejectWithValue('Pas de refresh token');
      }

      const response = await fetch('/api/v1/auth/token/refresh/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh: storedRefreshToken }),
      });

      if (!response.ok) {
        clearStoredAuth();
        return rejectWithValue('Refresh token invalide');
      }

      const data: LoginResponse = await response.json();
      const authPayload = {
        ...data,
        refresh: data.refresh || storedRefreshToken,
        user: data.user || readStoredUser() || undefined,
      } as LoginResponse;

      persistAuth(authPayload);

      return authPayload;
    } catch (error) {
      return rejectWithValue('Erreur de rafraîchissement');
    }
  }
);

const initialState: AuthState = {
  user: readStoredUser(),
  token: getStoredAccessToken(),
  refreshToken: getStoredRefreshToken(),
  isLoading: false,
  isAuthenticated: !!getStoredAccessToken(),
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(action.payload));
      state.isAuthenticated = Boolean(state.token || getStoredAccessToken());
    },
    setCredentials: (state, action: PayloadAction<LoginResponse>) => {
      persistAuth(action.payload);
      applyAuthPayload(state, action.payload);
      state.isLoading = false;
    },
    updateAccessToken: (state, action: PayloadAction<{ access: string; refresh?: string }>) => {
      const payload = {
        access: action.payload.access,
        refresh: action.payload.refresh || state.refreshToken || undefined,
        user: state.user || readStoredUser() || undefined,
      } as LoginResponse;
      persistAuth(payload);
      applyAuthPayload(state, payload);
      state.isAuthenticated = true;
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
      clearStoredAuth();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        persistAuth(action.payload);
        applyAuthPayload(state, action.payload);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      });

    builder
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        persistAuth(action.payload);
        applyAuthPayload(state, action.payload);
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      });

    builder
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
      });

    builder
      .addCase(refreshToken.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.isLoading = false;
        persistAuth(action.payload);
        applyAuthPayload(state, action.payload);
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        clearStoredAuth();
      });
  },
});

export const { clearError, setUser, setCredentials, updateAccessToken, clearAuth } = authSlice.actions;
export default authSlice.reducer;
